import { useDialogStore } from '@ariakit/react'
import type { FC } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { AnswerItem } from '@/components/commons/answer-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useChangeStep } from '@/features/experience-shell/hooks/use-change-step'
import { useExperienceShellFormContext } from '@/features/experience-shell/hooks/use-experience-shell-form-context.ts'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import type { EditExperiencePayload } from '../form-schemas'
import { useEditExperience } from '../queries/use-edit-experience'
import { StepKeyName, TransportingGuestsMode } from '../types'
import { Content, Footer, YesNoField } from './atoms'
import { SubmitModal } from './submit-modal'

export const LegalStep: FC<{
	experienceId: number
	galleryImagesCount: number
	isUpgrade: boolean
}> = ({ experienceId, galleryImagesCount, isUpgrade }) => {
	const { t } = useTranslation()
	const dialogStore = useDialogStore()
	const changeStep = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.legal,
		isUpgrade
	})
	const editExperienceMutation = useEditExperience({
		experienceId,
		options: {
			onError: (error) => errorHandler(error),
			onSuccess: () => dialogStore.setOpen(true)
		}
	})
	const {
		control,
		register,
		setValue,
		getValues,
		trigger,
		formState: { errors }
	} = useExperienceShellFormContext(isUpgrade)
	const categoryWatch = useWatch({
		name: 'experience.categoryKeyName',
		control
	})
	const transportingGuestsWatch = useWatch({
		name: 'legal.host_transports_guests',
		control
	})
	const pointOfInterestWatch = useWatch({
		name: 'legal.involves_point_of_interest',
		control
	})
	const foodWatch = useWatch({ name: 'legal.host_serve_food', control })
	const alcoholWatch = useWatch({ name: 'legal.host_serve_alcohol', control })

	return (
		<>
			<Content title={t('experience_shell:step_legal')}>
				<div className='flex flex-col gap-6'>
					{categoryWatch !== 'food_drink' &&
						categoryWatch !== 'entertainment' && (
							<>
								<Controller
									control={control}
									name={'legal.involves_national_park'}
									render={({ field }) => (
										<div className='flex w-full flex-col gap-2'>
											<YesNoField
												labelTitle={
													<b>
														{t(
															'experience_shell:legal_national_park_check'
														)}
													</b>
												}
												name={field.name}
												onChange={field.onChange}
												value={field.value}
											/>
											{errors.legal
												?.involves_national_park && (
												<InputHelpText
													error={t(
														'errors:field_required'
													)}
												/>
											)}
										</div>
									)}
								/>
								<Separator orientation='horizontal' />
								<Controller
									control={control}
									name={'legal.involves_point_of_interest'}
									render={({ field }) => (
										<div className='flex w-full flex-col gap-2'>
											<YesNoField
												labelTitle={
													<b>
														{t(
															'experience_shell:legal_point_of_interest_check'
														)}
													</b>
												}
												name={field.name}
												onChange={(value) => {
													field.onChange(value)
													setValue(
														'legal.host_discusses_poi_significance',
														undefined
													)
												}}
												value={field.value}
											/>
											{errors.legal
												?.involves_point_of_interest && (
												<InputHelpText
													error={t(
														'errors:field_required'
													)}
												/>
											)}
										</div>
									)}
								/>
								{pointOfInterestWatch && (
									<Controller
										control={control}
										name={
											'legal.host_discusses_poi_significance'
										}
										render={({ field }) => (
											<div className='flex w-full flex-col gap-2'>
												<YesNoField
													direction='column'
													labelTitle={t(
														'experience_shell:discuss_significance'
													)}
													name={field.name}
													onChange={field.onChange}
													value={field.value}
												/>
												{errors.legal
													?.host_discusses_poi_significance && (
													<InputHelpText
														error={t(
															'errors:field_required'
														)}
													/>
												)}
											</div>
										)}
									/>
								)}
								<Separator orientation='horizontal' />
							</>
						)}
					{categoryWatch === 'food_drink' && (
						<>
							<Controller
								control={control}
								name={'legal.host_serve_food'}
								render={({ field }) => (
									<div className='flex w-full flex-col gap-2'>
										<YesNoField
											labelTitle={
												<b>
													{t(
														'experience_shell:legal_food_check'
													)}
												</b>
											}
											name={field.name}
											onChange={(value) => {
												field.onChange(value)
												setValue(
													'legal.prepared_in_restaurant',
													undefined
												)
											}}
											value={field.value}
										/>
										{errors.legal?.host_serve_food && (
											<InputHelpText
												error={t(
													'errors:field_required'
												)}
											/>
										)}
									</div>
								)}
							/>
							{foodWatch && (
								<Controller
									control={control}
									name={'legal.prepared_in_restaurant'}
									render={({ field }) => (
										<div className='flex w-full flex-col gap-2'>
											<YesNoField
												direction='column'
												labelTitle={t(
													'experience_shell:legal_licensed_food'
												)}
												name={field.name}
												onChange={field.onChange}
												value={field.value}
											/>
											{errors.legal
												?.prepared_in_restaurant && (
												<InputHelpText
													error={t(
														'errors:field_required'
													)}
												/>
											)}
										</div>
									)}
								/>
							)}
							<Separator orientation='horizontal' />
						</>
					)}
					{(categoryWatch === 'food_drink' ||
						categoryWatch === 'entertainment') && (
						<>
							<Controller
								control={control}
								name={'legal.host_serve_alcohol'}
								render={({ field }) => (
									<div className='flex w-full flex-col gap-2'>
										<YesNoField
											labelTitle={
												<b>
													{t(
														'experience_shell:legal_alcohol_check'
													)}
												</b>
											}
											name={field.name}
											onChange={(value) => {
												field.onChange(value)
												setValue(
													'legal.is_licensed_establishment',
													undefined
												)
											}}
											value={field.value}
										/>
										{errors.legal?.host_serve_alcohol && (
											<InputHelpText
												error={t(
													'errors:field_required'
												)}
											/>
										)}
									</div>
								)}
							/>
							{alcoholWatch && (
								<Controller
									control={control}
									name={'legal.is_licensed_establishment'}
									render={({ field }) => (
										<div className='flex w-full flex-col gap-2'>
											<YesNoField
												direction='column'
												labelTitle={t(
													'experience_shell:legal_licensed_alcohol'
												)}
												name={field.name}
												onChange={field.onChange}
												value={field.value}
											/>
											{errors.legal
												?.is_licensed_establishment && (
												<InputHelpText
													error={t(
														'errors:field_required'
													)}
												/>
											)}
										</div>
									)}
								/>
							)}
						</>
					)}
					<Controller
						control={control}
						name={'legal.host_transports_guests'}
						render={({ field }) => (
							<div className='flex w-full flex-col gap-2'>
								<YesNoField
									labelTitle={
										<b>
											{t(
												'experience_shell:legal_transporting_guests_check'
											)}
										</b>
									}
									name={field.name}
									onChange={(value) => {
										field.onChange(value)
										setValue(
											'legal.host_transporting_guests_method',
											[]
										)
									}}
									value={field.value}
								/>
								<InputHelpText
									error={
										errors.legal?.host_transports_guests &&
										t('errors:field_required')
									}
								/>
							</div>
						)}
					/>
					{transportingGuestsWatch && (
						<div className='flex flex-col gap-2.5'>
							<p className='text-base text-neutral-900'>
								{t(
									'experience_shell:legal_transporting_guests_mode_description'
								)}
							</p>
							<div className='flex gap-3'>
								<AnswerItem
									inputSize='sm'
									label={t(
										'experience_shell:legal_transporting_guests_mode_car'
									)}
									type='checkbox'
									value={TransportingGuestsMode.enum.car}
									{...register(
										'legal.host_transporting_guests_method'
									)}
								/>
								<AnswerItem
									inputSize='sm'
									label={t(
										'experience_shell:legal_transporting_guests_mode_boat'
									)}
									type='checkbox'
									value={TransportingGuestsMode.enum.boat}
									{...register(
										'legal.host_transporting_guests_method'
									)}
								/>
								<AnswerItem
									inputSize='sm'
									label={t(
										'experience_shell:legal_transporting_guests_mode_plane'
									)}
									type='checkbox'
									value={TransportingGuestsMode.enum.plane}
									{...register(
										'legal.host_transporting_guests_method'
									)}
								/>
								<AnswerItem
									inputSize='sm'
									label={t(
										'experience_shell:legal_transporting_guests_mode_motorcycle'
									)}
									type='checkbox'
									value={
										TransportingGuestsMode.enum.motorcycle
									}
									{...register(
										'legal.host_transporting_guests_method'
									)}
								/>
							</div>
							{errors.legal?.host_transporting_guests_method && (
								<InputHelpText
									error={t('errors:field_required')}
								/>
							)}
						</div>
					)}
					<Separator orientation='horizontal' />
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={
						changeStep.isBackLoading ||
						editExperienceMutation.isPending
					}
					onClick={() =>
						changeStep.handleChangeStep({
							isBack: true,
							stepToGoTo: StepKeyName.enum.pricing_discounts
						})
					}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{changeStep.isBackLoading && <Spinner />}
				</ButtonLegacy>
				<SubmitModal
					experienceId={experienceId}
					galleryImagesCount={galleryImagesCount}
					isUpgrade={isUpgrade}
					store={dialogStore}
					trigger={
						<ButtonLegacy
							disabled={editExperienceMutation.isPending}
							onClick={async (event) => {
								event.preventDefault()
								if (
									isUpgrade &&
									!(await trigger(StepKeyName.enum.legal))
								) {
									return
								}
								const {
									categoryKeyName: _categoryKeyName,
									...rest
								} = getValues(StepKeyName.enum.legal)
								editExperienceMutation.mutate({
									...rest
								} as EditExperiencePayload)
							}}
							type='button'
							variant='primarySuccess'
						>
							{t('experience_shell:submit_to_host')}
							<ArrowRightIcon />
						</ButtonLegacy>
					}
				/>
			</Footer>
		</>
	)
}
